import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, makeStyles, Button, Checkbox } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import {
  ideaActions,
  portfolioExecutionFlow,
  personalizedClassificationActions,
  ideaCreateActions,
  fluxOfIdeasActions,
  engagementActions
} from '../../actions';
import { BackNavAdmin, IdeaDetailsLayout, FluxOfIdeasLayout } from '../../components';
import { useIdeaDetailsController } from './hooks/useIdeaDetailsController';
import { useCanEditableIdea } from '../../hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingLeft: 14,
      paddingRight: 14,
    },
  },
}));

export const IdeaDetailsPage = () => {
  const { id } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {defaultCategories, ideaDetailsFlux} = useSelector(state => state.fluxIdeas);
  const { typeProfile, _id: userId } = useSelector((state) => state.user);
  const {
    stepflux,
    hasIdeaFlux,
    hasLoadingFlux,
    handleEditIdea,
    handleSendIdea,
    handleDeactivateIdea
  } = useIdeaDetailsController();
  const buls = hasIdeaFlux?.businessUnitLocal?.map(item => item)[0];
  
  useEffect(() => {
    if (!Array.isArray(defaultCategories)) return;

    let selectedCategoriesById = ideaDetailsFlux?.categories?.map(item => item._id) ?? [];

    if (defaultCategories) {
      const updated = defaultCategories.map(item => ({
        _id: item._id,
        label: item.title,
        checked: selectedCategoriesById.includes(item._id) ?? false
      }));
  
      dispatch(ideaActions.loadCategories(updated));
    }
  }, [defaultCategories, ideaDetailsFlux]);
  
  useEffect(() => {
    if (stepflux) {
      dispatch(fluxOfIdeasActions.getIdeaInFlux(id));
    } else {
      dispatch(ideaActions.getIdeaDetails(id));
    }

    dispatch(ideaActions.getPossibleCategories());
    dispatch(ideaActions.clearApprovedDetails());
    dispatch(portfolioExecutionFlow.getProjects(id));
    dispatch(personalizedClassificationActions.getPersonalizedClassification());

    dispatch(engagementActions.getEngagement("up", id));
    dispatch(engagementActions.getEngagement("comment", id));
  }, [dispatch, id]);

  useEffect(() => {
    if (hasIdeaFlux?.statusIdea?.code === 'DRAFT') {
      dispatch(ideaCreateActions.loadIdeaDetailsDraft(hasIdeaFlux));

      return () => { dispatch(ideaCreateActions.clearAll()); }
    }
  }, [hasIdeaFlux]);

  useEffect(() => {
    return () => dispatch(ideaActions.clearIdeaDetails());
  }, [dispatch]);

  const canEditIdea = () => {
    const isContributor = hasIdeaFlux?.contributors?.some(item => item?._id === userId);
    const isOwner = hasIdeaFlux?.user?._id === userId;
    const isAdmin = typeProfile.type === 'admin';
    const stepId = ideaDetailsFlux?.currentStepFlux?.ideaStepFlux?._id;

    const stepHasEditableIdea = useCanEditableIdea(stepId);
    const rolesVerify = (isContributor || isOwner || isAdmin);

    if (stepflux) {
      return rolesVerify && stepHasEditableIdea;
    }

    return (
      rolesVerify &&
      hasIdeaFlux?.statusIdea?.code !== 'CANCELED' &&
      hasIdeaFlux?.statusIdea?.code !== 'CONCLUDED' &&
      hasIdeaFlux?.statusIdea?.code !== 'UNQUALIFIED' 
    );
  };

  const shouldRenderEditButton = canEditIdea();

  return (
    <>
      <BackNavAdmin title={hasLoadingFlux ? 'carregando...' : hasIdeaFlux?.name}>
        {(typeProfile.type === 'admin') && (
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 16 }}>
            <Checkbox
              checked={hasIdeaFlux.isActive !== false}
              onChange={handleDeactivateIdea}
              color="primary"
              disabled={hasLoadingFlux}
              inputProps={{ 'aria-label': t('ativar_ideia') }}
            />
            <span style={{ marginLeft: 8 }}>
              {t('ideia_ativa')}
            </span>
          </div>
        )}
        {(typeProfile.type === 'admin' ||
          (hasIdeaFlux?.user?._id === userId && 
          hasIdeaFlux?.statusIdea?.code === 'DRAFT') || 
          shouldRenderEditButton
        ) && (
          <Button
            color="primary"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditIdea}
            disabled={hasLoadingFlux}
          >
            {t('editar_ideia')}
          </Button>
        )}
        {hasIdeaFlux?.statusIdea?.code === 'DRAFT' && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleSendIdea}
            disabled={hasLoadingFlux || !buls || buls === undefined}
            style={{ marginLeft: 10 }}
          >
            {t('enviar')}
          </Button>
        )}
      </BackNavAdmin>
      <Container maxWidth="lg" className={classes.root}>
        {stepflux ? <FluxOfIdeasLayout /> : <IdeaDetailsLayout />}
      </Container>
    </>
  );
}
